import React, { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import type { ClientRecord, FormErrors } from "../types";
import { HOSPITAL_FACILITIES } from "../data/lookups";
import { Field, Select, TextInput } from "../components/FormControls";
import { fetchGeneratedReports, generateReport, type GeneratedReportApi } from "../api/client";

const REPORT_TYPES: string[] = [
  "Case Summary Report",
  "Linkage to Care Report",
  "FOCUS Program Report",
  "Facility Activity Report",
  "Pending Cases Report",
  "HIV/STD Test Results Report",
];
const REPORT_FORMATS: string[] = ["PDF", "Excel", "CSV"];

interface ReportsPageProps {
  records: ClientRecord[];
}

export function ReportsPage({ records }: ReportsPageProps) {
  const [reports, setReports] = useState<GeneratedReportApi[]>([]);
  const [reportType, setReportType] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [facility, setFacility] = useState<string>("All");
  const [format, setFormat] = useState<string>("PDF");
  const [errors, setErrors] = useState<FormErrors>({});
  const [generating, setGenerating] = useState<boolean>(false);

  useEffect(() => {
    fetchGeneratedReports()
      .then(setReports)
      .catch((err) => console.warn("Could not load generated reports from the API.", err));
  }, []);

  const quickStats = useMemo(
    () => ({
      totalCases: records.length,
      pending: records.filter((r) => r.linkageGridStatus === "Pending").length,
    }),
    [records]
  );

  async function handleGenerate() {
    const e: FormErrors = {};
    if (!reportType) e.reportType = "Report Type is Required";
    if (!dateFrom) e.dateFrom = "Start Date is Required";
    if (!dateTo) e.dateTo = "End Date is Required";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setGenerating(true);
    try {
      const saved = await generateReport({ reportType, dateFrom, dateTo, facility, format });
      setReports((prev) => [saved, ...prev]);
    } catch (err) {
      console.error("Could not generate report via the API", err);
      setReports((prev) => [
        {
          name: `${reportType} (${dateFrom} to ${dateTo})`,
          type: reportType,
          dateRangeFrom: dateFrom,
          dateRangeTo: dateTo,
          generatedDate: new Date().toLocaleString("en-US"),
          generatedBy: "Admin User",
          format,
        },
        ...prev,
      ]);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="p-3 md:p-4">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-slate-900">Reports</h2>
        <p className="text-xs text-slate-500">Generate and download program reports across all facilities in the registry.</p>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xl font-semibold text-slate-900">{quickStats.totalCases}</p>
          <p className="text-xs text-slate-500">Total cases in registry</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xl font-semibold text-slate-900">{quickStats.pending}</p>
          <p className="text-xs text-slate-500">Pending linkage cases</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xl font-semibold text-slate-900">{reports.length}</p>
          <p className="text-xs text-slate-500">Reports generated</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xl font-semibold text-slate-900">{REPORT_TYPES.length}</p>
          <p className="text-xs text-slate-500">Report types available</p>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-slate-800">Generate a Report</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Report Type" required error={errors.reportType}>
            <Select value={reportType} error={errors.reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="">Choose...</option>
              {REPORT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </Select>
          </Field>
          <Field label="Start Date" required error={errors.dateFrom}>
            <TextInput type="date" value={dateFrom} error={errors.dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="End Date" required error={errors.dateTo}>
            <TextInput type="date" value={dateTo} error={errors.dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <Field label="Facility">
            <Select value={facility} onChange={(e) => setFacility(e.target.value)}>
              <option>All</option>
              {Object.keys(HOSPITAL_FACILITIES).map((h) => <option key={h}>{h}</option>)}
            </Select>
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <div className="w-full sm:w-48">
            <Field label="Format">
              <Select value={format} onChange={(e) => setFormat(e.target.value)}>
                {REPORT_FORMATS.map((f) => <option key={f}>{f}</option>)}
              </Select>
            </Field>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {generating ? "Generating…" : "Generate Report"}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-800">Generated Reports</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Report Name</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Date Range</th>
                <th className="px-4 py-2.5">Generated</th>
                <th className="px-4 py-2.5">Format</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r, i) => (
                <tr key={r.id ?? i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.type}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.dateRangeFrom} – {r.dateRangeTo}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.generatedDate} · {r.generatedBy}</td>
                  <td className="px-4 py-2.5">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{r.format}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <a
                      href="#"
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()}
                      className="inline-flex items-center gap-1.5 rounded-full border border-cyan-600 px-3.5 py-1 text-xs font-medium text-cyan-700 hover:bg-cyan-50"
                    >
                      <Download className="h-3 w-3" /> Download
                    </a>
                  </td>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No reports generated yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
